/**
 * PlotPlanEditor — implements `Capexara Process Industry_ProcessPlant_Complete_Architecture.md` §6.3 D.1.
 *
 * 2D plot plan: draggable equipment footprints on a grid. Equipment comes
 * from PDO; positions persisted at pdo.plot_plan.footprints[]. A 3D toggle
 * renders the same footprints as extruded blocks (simple isometric).
 */
import React, { useEffect, useRef, useState } from "react";
import { Loader2, Save, Ruler, Grid3x3, Box, Square, Sparkles, Download, AlertCircle, CheckCircle2 } from "lucide-react";
import api, { formatErr } from "../lib/api";

const DEFAULT_SIZE = { vessel: [40, 60], column: [40, 40], heat_exchanger: [60, 30],
                       pump: [30, 20], compressor: [50, 30], fired_heater: [80, 80],
                       tank: [80, 80], air_cooler: [60, 40], cooler: [50, 30] };
const DEFAULT_COLOUR = "#3CA0FF";

export default function PlotPlanEditor({ projectId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [equipment, setEquipment] = useState([]);    // [{tag, name, type, x, y, rot, w, h}]
  const [view, setView] = useState("2d");            // 2d | 3d
  const [snap, setSnap] = useState(10);
  const [selTag, setSelTag] = useState(null);
  const [drag, setDrag] = useState(null);            // {tag, dx, dy}
  const svgRef = useRef(null);
  const [toast, setToast] = useState(null);
  // Nano Banana enrichment state
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [enrichedUrl, setEnrichedUrl] = useState(null);
  const [enrichMeta, setEnrichMeta] = useState(null);
  const [enrichErr, setEnrichErr] = useState(null);
  const [hires, setHires] = useState(false);
  // Simulation data provenance banner
  const [provenance, setProvenance] = useState(null);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId]);

  // Fetch sim-data provenance + any cached enrichment on mount
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const r = await api.get(`/projects/${projectId}/simulation/import/provenance`);
        setProvenance(r.data);
      } catch { /* endpoint may be unmounted on some deployments */ }
      try {
        const s = await api.get(`/projects/${projectId}/plot-plan.png/enrich/status`);
        if (s.data && s.data.png_base64 !== undefined) {/* noop */}
        if (s.data && s.data.generated_at) {
          setEnrichMeta(s.data);
          // Load the actual image URL (blob-fetch through axios so cookie auth works)
          const img = await api.get(`/projects/${projectId}/plot-plan.png/enrich/latest`, { responseType: "blob" });
          setEnrichedUrl(URL.createObjectURL(img.data));
        }
      } catch { /* no enrichment yet — normal */ }
    })();
  }, [projectId]);

  const enrichWithAI = async () => {
    setEnrichBusy(true); setEnrichErr(null);
    try {
      const r = await api.post(
        `/projects/${projectId}/plot-plan.png/enrich`,
        { plot_size_m: [60, 40], drawing_no: "CPS-14-PP-001", revision: "A", hires },
        { responseType: "blob", timeout: 180000 });
      // Turn the blob into an object URL so <img src=> can show it
      setEnrichedUrl(URL.createObjectURL(r.data));
      // Pull metadata from response headers
      setEnrichMeta({
        model:      r.headers?.["x-enrichment-model"],
        elapsed_s:  r.headers?.["x-enrichment-elapsed-s"],
        generated_at: new Date().toISOString(),
      });
    } catch (e) {
      setEnrichErr(formatErr(e?.response?.data?.detail) || e?.message
                        || "Enrichment failed — try again in a minute.");
    } finally {
      setEnrichBusy(false);
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/pdo`);
      const pdo = r.data || {};
      const saved = ((pdo.plot_plan || {}).footprints) || [];
      const eqRaw = pdo.equipment;
      const eqList = [];
      if (Array.isArray(eqRaw)) {
        for (const e of eqRaw) if (e?.tag && !e.deleted) eqList.push({ ...e, tag: e.tag });
      } else if (eqRaw && typeof eqRaw === "object") {
        for (const [k, v] of Object.entries(eqRaw))
          if (v && !v.deleted) eqList.push({ ...v, tag: v.tag || k });
      }
      // Merge positions
      const byTag = new Map(saved.map((f) => [f.tag, f]));
      let ix = 0;
      const merged = eqList.map((e) => {
        const fp = byTag.get(e.tag);
        const [dw, dh] = DEFAULT_SIZE[(e.type || "").toLowerCase()] || [50, 50];
        const x = fp?.x ?? (60 + (ix % 8) * 140);
        const y = fp?.y ?? (60 + Math.floor(ix / 8) * 110);
        ix++;
        return {
          tag: e.tag,
          name: e.name || e.tag,
          type: (e.type || "").toLowerCase(),
          x, y,
          rot: fp?.rot ?? 0,
          w: fp?.w ?? dw,
          h: fp?.h ?? dh,
        };
      });
      setEquipment(merged);
    } catch (e) {
      setToast({ kind: "err", text: formatErr(e?.response?.data?.detail) || e.message });
    } finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/projects/${projectId}/pdo/plot_plan`, {
        patch: {
          footprints: equipment.map(({ tag, x, y, rot, w, h, type }) =>
            ({ tag, x, y, rot, w, h, type })),
          updated_at: new Date().toISOString(),
        },
      });
      setToast({ kind: "ok", text: `Saved ${equipment.length} equipment footprints.` });
      setTimeout(() => setToast(null), 3000);
    } catch (e) {
      setToast({ kind: "err", text: formatErr(e?.response?.data?.detail) || e.message });
    } finally { setSaving(false); }
  };

  const startDrag = (e, tag) => {
    e.stopPropagation();
    setSelTag(tag);
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const svgX = e.clientX - rect.left;
    const svgY = e.clientY - rect.top;
    const eq = equipment.find((x) => x.tag === tag);
    if (!eq) return;
    setDrag({ tag, dx: svgX - eq.x, dy: svgY - eq.y });
  };
  const onMove = (e) => {
    if (!drag) return;
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const svgX = e.clientX - rect.left - drag.dx;
    const svgY = e.clientY - rect.top - drag.dy;
    const sx = Math.round(svgX / snap) * snap;
    const sy = Math.round(svgY / snap) * snap;
    setEquipment((xs) => xs.map((eq) =>
      eq.tag === drag.tag ? { ...eq, x: sx, y: sy } : eq));
  };
  const endDrag = () => setDrag(null);

  const rotate = () => {
    if (!selTag) return;
    setEquipment((xs) => xs.map((eq) =>
      eq.tag === selTag ? { ...eq, rot: (eq.rot + 90) % 360 } : eq));
  };

  if (loading) return <div style={{ padding: 20 }}><Loader2 className="pf-spin" size={16}/> Loading plot plan…</div>;

  return (
    <div data-testid="plot-plan-editor" style={{ display: "flex", flexDirection: "column", height: "100%", padding: 12, gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        <h2 className="pf-heading" style={{ margin: 0 }}>Plot Plan Editor</h2>
        <span className="pf-overline" style={{ color: "var(--pf-text-dim)", margin: 0 }}>Stage D.1 · 2D Layout</span>
        <div style={{ flex: 1 }}/>
        <button data-testid="plot-plan-toggle-2d" className="btn btn-ghost" onClick={() => setView("2d")} style={{ color: view === "2d" ? "var(--pf-accent)" : undefined, borderColor: view === "2d" ? "var(--pf-accent)" : undefined }}><Square size={12}/> 2D</button>
        <button data-testid="plot-plan-toggle-3d" className="btn btn-ghost" onClick={() => setView("3d")} style={{ color: view === "3d" ? "var(--pf-accent)" : undefined, borderColor: view === "3d" ? "var(--pf-accent)" : undefined }}><Box size={12}/> 3D</button>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
          <Grid3x3 size={12}/> Snap
          <select value={snap} onChange={(e) => setSnap(Number(e.target.value))} style={{ fontSize: 11, padding: "2px 4px" }}>
            <option value={5}>5</option><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
          </select>
        </label>
        <button data-testid="plot-plan-rotate" className="btn btn-ghost" onClick={rotate} disabled={!selTag}><Ruler size={12}/> Rotate 90°</button>
        <button data-testid="plot-plan-save" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? <Loader2 className="pf-spin" size={12}/> : <Save size={12}/>} Save
        </button>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, marginLeft: 8 }}>
          <input type="checkbox" checked={hires} onChange={(e) => setHires(e.target.checked)} data-testid="plot-plan-hires-toggle"/>
          Hi-res (slower)
        </label>
        <button
          data-testid="plot-plan-ai-enrich"
          className="btn btn-primary"
          onClick={enrichWithAI}
          disabled={enrichBusy || equipment.length === 0}
          title="Send the vector layout to Gemini Nano Banana. Produces a photorealistic industry-standard rendering with equipment glyphs, road hatching, fire zones, escape routes, coordinate grid, north arrow and legend. Takes 15-40s."
          style={{ background: enrichBusy ? "var(--pf-border)" : "linear-gradient(135deg,#8b5cf6,#ec4899)",
                       border: "none", color: "#fff" }}>
          {enrichBusy ? <><Loader2 className="pf-spin" size={12}/> Enriching…</>
                       : <><Sparkles size={12}/> Enrich with AI</>}
        </button>
      </div>

      {/* Simulation-data provenance banner — tells the user whether the
             PFDs/P&IDs are actually being driven by their uploaded Excel */}
      {provenance && (
        <div
          data-testid="plot-plan-provenance-banner"
          style={{
            padding: "8px 12px", fontSize: 12, borderRadius: 6,
            border: `1px solid ${
              provenance.source === "external_import" ? "#10b981" :
              provenance.source === "internal_solver" ? "#3b82f6" :
              provenance.source === "external_import_not_committed" ? "#f59e0b" :
              "#ef4444"}`,
            background: `${
              provenance.source === "external_import" ? "#10b98118" :
              provenance.source === "internal_solver" ? "#3b82f618" :
              provenance.source === "external_import_not_committed" ? "#f59e0b18" :
              "#ef444418"}`,
            color: "var(--pf-text)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
          {provenance.source === "external_import"
            ? <CheckCircle2 size={14} style={{ color: "#10b981" }}/>
            : <AlertCircle  size={14} style={{ color:
                provenance.source === "internal_solver" ? "#3b82f6" :
                provenance.source === "external_import_not_committed" ? "#f59e0b" :
                "#ef4444" }}/>}
          <span style={{ fontWeight: 600, fontSize: 11, letterSpacing: 0.4 }}>
            SIM DATA:
          </span>
          <span>{provenance.message}</span>
        </div>
      )}

      <div style={{ fontSize: 11, color: "var(--pf-text-dim)" }}>
        {equipment.length} equipment footprints · click to select, drag to move, Rotate button turns 90°. Grid = {snap}px.
      </div>

      <div style={{ flex: 1, minHeight: 0, border: "1px solid var(--pf-border)", background: "var(--pf-blueprint)", overflow: "hidden", position: "relative" }}>
        {equipment.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--pf-text-dim)" }}>
            No equipment in PDO yet. Run Stage B.1 (Process Simulation) first, then return here.
          </div>
        ) : view === "2d" ? (
          <svg
            ref={svgRef}
            viewBox="0 0 1400 900"
            width="100%" height="100%"
            onMouseMove={onMove}
            onMouseUp={endDrag}
            onMouseLeave={endDrag}
            onClick={() => setSelTag(null)}
            data-testid="plot-plan-svg-2d"
          >
            <defs>
              <pattern id="ppgrid" width={snap} height={snap} patternUnits="userSpaceOnUse">
                <path d={`M ${snap} 0 L 0 0 0 ${snap}`} fill="none" stroke="#1a2030" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="1400" height="900" fill="url(#ppgrid)"/>
            {/* Plot boundary */}
            <rect x="20" y="20" width="1360" height="860" fill="none" stroke="#6b7280" strokeDasharray="8,4" strokeWidth="1"/>
            {/* Equipment */}
            {equipment.map((eq) => {
              const selected = eq.tag === selTag;
              return (
                <g key={eq.tag}
                   data-testid={`plot-plan-eq-${eq.tag}`}
                   transform={`translate(${eq.x} ${eq.y}) rotate(${eq.rot} ${eq.w / 2} ${eq.h / 2})`}
                   onMouseDown={(e) => startDrag(e, eq.tag)}
                   style={{ cursor: "move" }}>
                  <rect x="0" y="0" width={eq.w} height={eq.h}
                        fill={selected ? "rgba(255,176,0,0.25)" : "rgba(60,160,255,0.15)"}
                        stroke={selected ? "#FFB000" : DEFAULT_COLOUR}
                        strokeWidth={selected ? 2 : 1.5}/>
                  <text x={eq.w / 2} y={eq.h / 2 + 4}
                        fill={selected ? "#FFB000" : DEFAULT_COLOUR}
                        fontFamily="'JetBrains Mono', monospace" fontSize="11"
                        textAnchor="middle" fontWeight={selected ? 700 : 500}>{eq.tag}</text>
                </g>
              );
            })}
          </svg>
        ) : (
          // Simple isometric 3D projection
          <svg viewBox="0 0 1400 900" width="100%" height="100%" data-testid="plot-plan-svg-3d">
            <rect width="1400" height="900" fill="#0E1218"/>
            {[...equipment]
              .sort((a, b) => (a.x + a.y) - (b.x + b.y))
              .map((eq) => {
                const depth = (eq.type === "column" || eq.type === "tank") ? 120 :
                              (eq.type === "fired_heater") ? 140 :
                              (eq.type === "vessel") ? 80 : 40;
                const ox = 60, oy = 60;
                const sx = eq.x * 0.8 + ox;
                const sy = eq.y * 0.6 + oy - depth * 0.4;
                const w = eq.w * 0.8, h = eq.h * 0.6;
                const top = [[sx, sy], [sx + w, sy], [sx + w + 20, sy - 20], [sx + 20, sy - 20]];
                const front = [[sx, sy + h], [sx + w, sy + h], [sx + w, sy], [sx, sy]];
                const right = [[sx + w, sy + h], [sx + w + 20, sy + h - 20], [sx + w + 20, sy - 20], [sx + w, sy]];
                return (
                  <g key={eq.tag} data-testid={`plot-plan-3d-eq-${eq.tag}`}>
                    <polygon points={front.map((p) => p.join(",")).join(" ")} fill="rgba(60,160,255,0.35)" stroke="#3CA0FF"/>
                    <polygon points={right.map((p) => p.join(",")).join(" ")} fill="rgba(60,160,255,0.20)" stroke="#3CA0FF"/>
                    <polygon points={top.map((p) => p.join(",")).join(" ")} fill="rgba(60,160,255,0.55)" stroke="#3CA0FF"/>
                    <text x={sx + w / 2} y={sy + h / 2 + 4} fill="#FFB000"
                          fontFamily="'JetBrains Mono', monospace" fontSize="10" textAnchor="middle" fontWeight="700">{eq.tag}</text>
                  </g>
                );
              })}
          </svg>
        )}
      </div>

      {/* ── AI-Enriched Plot Plan (Nano Banana) ─────────────────── */}
      {(enrichedUrl || enrichErr) && (
        <div data-testid="plot-plan-enriched-panel"
             style={{ marginTop: 12, border: "1px solid var(--pf-border)",
                          borderRadius: 6, overflow: "hidden",
                          background: "var(--pf-panel)" }}>
          <div style={{ padding: "8px 12px", display: "flex",
                             alignItems: "center", gap: 10, flexWrap: "wrap",
                             borderBottom: "1px solid var(--pf-border)",
                             background: "linear-gradient(90deg,#8b5cf618,#ec489918)" }}>
            <Sparkles size={14} style={{ color: "#a78bfa" }}/>
            <span className="pf-overline" style={{ margin: 0, color: "#a78bfa" }}>
              AI-ENRICHED PLOT PLAN (Nano Banana)
            </span>
            {enrichMeta?.model && (
              <span style={{ fontSize: 11, color: "var(--pf-text-dim)",
                                fontFamily: "'JetBrains Mono', monospace" }}>
                {enrichMeta.model} · {enrichMeta.elapsed_s}s
              </span>
            )}
            <div style={{ flex: 1 }}/>
            {enrichedUrl && (
              <a href={enrichedUrl}
                    download={`plot-plan-enriched-${new Date().toISOString().slice(0,10)}.png`}
                    className="btn btn-ghost"
                    data-testid="plot-plan-enriched-download"
                    style={{ fontSize: 11 }}>
                <Download size={12}/> Download
              </a>
            )}
          </div>
          {enrichErr && (
            <div style={{ padding: 16, fontSize: 12, color: "#fca5a5",
                              background: "#ef444418" }}>
              {enrichErr}
            </div>
          )}
          {enrichedUrl && (
            <img
              data-testid="plot-plan-enriched-image"
              src={enrichedUrl}
              alt="AI-enriched plot plan"
              style={{ width: "100%", display: "block", background: "#f5efe3" }}
            />
          )}
        </div>
      )}
      {toast && (
        <div data-testid={`plot-plan-toast-${toast.kind}`} style={{
          position: "fixed", bottom: 30, left: "50%", transform: "translateX(-50%)",
          background: toast.kind === "ok" ? "rgba(34,197,94,0.18)" : "rgba(239,68,68,0.18)",
          border: `1px solid ${toast.kind === "ok" ? "#22c55e" : "#ef4444"}`,
          color: toast.kind === "ok" ? "#86efac" : "#fca5a5",
          padding: "8px 16px", fontSize: 12, zIndex: 10001,
        }}>{toast.text}</div>
      )}
    </div>
  );
}
